import { useCallback, useEffect, useState } from "react";
import { BadgeCheck, Clock, ExternalLink, Loader2, Pause, Pencil, Play, Trash2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = { name: "", link: "", logo_url: "", contact_name: "", email: "", phone: "", package: "", joined_at: "", expires_at: "", contra: false };
const inputCls =
  "w-full bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3 text-clay-text placeholder:text-clay-muted/60 transition-colors duration-300";

export default function AdminSponsors({ token }) {
  const [sponsors, setSponsors] = useState([]);
  const [signups, setSignups] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [logo, setLogo] = useState(null);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [s, su] = await Promise.all([
        api.get("/admin/sponsors", { headers }),
        api.get("/admin/sponsor-signups", { headers }),
      ]);
      setSponsors(s.data);
      setSignups(su.data);
    } catch {
      toast.error("Failed to load sponsors");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const imgSrc = (u) => (u?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Sponsor name is required");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, k === "contra" ? String(v) : v));
      if (logo) fd.append("logo", logo);
      if (editId) {
        await api.put(`/admin/sponsors/${editId}`, fd, { headers, timeout: 120000 });
        toast.success("Sponsor updated");
      } else {
        await api.post("/admin/sponsors", fd, { headers, timeout: 120000 });
        toast.success(`${form.name} added as a sponsor`);
      }
      setForm(EMPTY);
      setLogo(null);
      setEditId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const edit = (s) => {
    setForm({
      name: s.name || "",
      link: s.link || "",
      logo_url: s.logo?.startsWith("/api") ? "" : s.logo || "",
      contact_name: s.contact_name || "",
      email: s.email || "",
      phone: s.phone || "",
      package: s.package || "",
      joined_at: s.joined_at || "",
      expires_at: s.expires_at || "",
      contra: s.contra === true,
    });
    setLogo(null);
    setEditId(s.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggle = async (s) => {
    try {
      const { data } = await api.post(`/admin/sponsors/${s.id}/toggle`, {}, { headers });
      toast.success(data.status === "active" ? `${s.name} reactivated` : `${s.name} suspended — hidden from the site`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name} from sponsors?`)) return;
    try {
      await api.delete(`/admin/sponsors/${s.id}`, { headers });
      toast.success(`${s.name} removed`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const relist = async (s) => {
    if (!window.confirm(`Relist ${s.name} as a paid sponsor? They'll show as paid for the next 12 months and go back on the site.`)) return;
    try {
      const { data } = await api.post(`/admin/sponsors/${s.id}/relist`, {}, { headers });
      toast.success(`${s.name} relisted — paid up to ${data.expires_at}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const markPaid = async (s) => {
    try {
      const { data } = await api.post(`/admin/sponsors/${s.id}/paid`, {}, { headers });
      toast.success(data.status === "paid" ? `${s.name} marked as paid` : `${s.name} set back to pending`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportCsv = async (kind) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/sponsors/export.csv?list=${kind}`, { headers });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sponsors_${kind}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const removeSignup = async (s) => {
    if (!window.confirm(`Delete signup from ${s.business}?`)) return;
    try {
      await api.delete(`/admin/sponsor-signups/${s.id}`, { headers });
      toast.success("Signup deleted");
      setSignups((prev) => prev.filter((x) => x.id !== s.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const fmtDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
  const current = sponsors.filter((s) => s.active !== false);
  const historic = sponsors.filter((s) => s.active === false);

  const cellCls = "px-4 py-3 text-clay-muted text-xs align-top";
  const actionBtn = "transition-colors p-1.5";

  const sponsorRow = (s, isHistoric) => {
    const days = s.expires_at ? Math.ceil((new Date(s.expires_at) - new Date()) / 86400000) : null;
    return (
      <tr key={s.id} data-testid="sponsor-admin-row" className="border-b border-clay-border hover:bg-clay-card/60 transition-colors">
        <td className="px-4 py-3 align-top">
          <div className="flex items-center gap-3">
            <span className="bg-white/95 w-14 h-10 flex items-center justify-center p-1 shrink-0">
              <img src={imgSrc(s.logo)} alt="" className="max-h-full max-w-full object-contain" loading="lazy" />
            </span>
            <span className="font-display font-bold uppercase tracking-wide text-sm">{s.name}</span>
          </div>
        </td>
        <td className={cellCls}>{s.package || "—"}</td>
        <td className={cellCls}>
          {isHistoric ? (
            <span className="font-mono text-[10px] uppercase tracking-wide text-red-400">{s.lapsed ? "Lapsed — not renewed" : "Suspended"}</span>
          ) : (
            <span
              className={`font-mono text-[10px] uppercase tracking-wide px-1.5 py-0.5 border ${
                s.status === "contra"
                  ? "border-sky-500/40 text-sky-400"
                  : s.status === "paid"
                    ? "border-green-500/40 text-green-400"
                    : "border-amber-500/40 text-amber-400"
              }`}
              data-testid="sponsor-paid-badge"
            >
              {s.status === "contra" ? "Contra" : s.status === "paid" ? "Paid" : "Pending"}
            </span>
          )}
        </td>
        <td className={cellCls}>{s.joined_at ? fmtDate(s.joined_at) : "—"}</td>
        <td className={cellCls} data-testid="sponsor-renewal-cell">
          {s.expires_at ? (
            <>
              {fmtDate(s.expires_at)}
              {days !== null && days < 0 && <span className="block font-mono text-[9px] uppercase tracking-wide text-red-400">Expired</span>}
              {days !== null && days >= 0 && days <= 30 && <span className="block font-mono text-[9px] uppercase tracking-wide text-amber-400">Due in {days}d</span>}
            </>
          ) : (
            "—"
          )}
        </td>
        <td className={cellCls}>
          {s.contact_name && <span className="block">{s.contact_name}</span>}
          {s.email && <span className="block">{s.email}</span>}
          {s.phone && <span className="block">{s.phone}</span>}
          {!s.contact_name && !s.email && !s.phone && "—"}
        </td>
        <td className="px-4 py-3 align-top">
          <div className="flex gap-1">
            {isHistoric && (
              <button data-testid="sponsor-relist-button" onClick={() => relist(s)} className={`${actionBtn} text-green-400/80 hover:text-green-300`} aria-label="Relist sponsor as paid">
                <Play className="w-4 h-4" />
              </button>
            )}
            <button data-testid="sponsor-edit-button" onClick={() => edit(s)} className={`${actionBtn} text-clay-muted hover:text-clay-gold`} aria-label="Edit sponsor">
              <Pencil className="w-4 h-4" />
            </button>
            {!isHistoric && s.status !== "contra" && s.status !== "paid" && (
              <button data-testid="sponsor-mark-paid-button" onClick={() => markPaid(s)} className={`${actionBtn} text-clay-muted hover:text-green-400`} aria-label="Mark sponsor as paid">
                <BadgeCheck className="w-4 h-4" />
              </button>
            )}
            {!isHistoric && s.status !== "contra" && s.status === "paid" && (
              <button data-testid="sponsor-mark-paid-button" onClick={() => markPaid(s)} className={`${actionBtn} text-green-400 hover:text-clay-muted`} aria-label="Set sponsor back to pending">
                <BadgeCheck className="w-4 h-4" />
              </button>
            )}
            {!isHistoric && (
              <button data-testid="sponsor-suspend-button" onClick={() => toggle(s)} className={`${actionBtn} text-clay-muted hover:text-amber-400`} aria-label="Suspend sponsor">
                <Pause className="w-4 h-4" />
              </button>
            )}
            <button data-testid="sponsor-delete-button" onClick={() => remove(s)} className={`${actionBtn} text-clay-muted hover:text-red-400`} aria-label="Delete sponsor">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10 space-y-12">
      <section>
        <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight mb-5">
          Sponsor Signups <span className="text-clay-muted text-xl">({signups.length})</span>
        </h2>
        {signups.length === 0 ? (
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay-muted border border-dashed border-clay-border py-12 text-center">
            No signups yet — packages are live on the Sponsors page
          </p>
        ) : (
          <div className="space-y-3">
            {signups.map((s) => (
              <div key={s.id} data-testid="sponsor-signup-row" className="border border-clay-border bg-clay-card p-5 flex flex-wrap items-center gap-4">
                <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${s.status === "paid" ? "border-green-500/40 text-green-400" : "border-amber-500/40 text-amber-400"}`}>
                  {s.status === "paid" ? <span className="flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> Paid</span> : <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-display font-bold uppercase tracking-wide">
                    {s.business} <span className="text-clay-gold font-mono text-xs ml-2">{s.package_label}</span>
                  </p>
                  <p className="text-clay-muted text-xs mt-1">
                    {s.contact_name} · {s.email} · {s.phone} {s.first_time && "· +$350 sign fee"} · ${s.total?.toFixed(2)}
                  </p>
                  {s.message && <p className="text-clay-muted text-xs mt-1 italic">"{s.message}"</p>}
                </div>
                <button data-testid="signup-delete-button" onClick={() => removeSignup(s)} className="text-clay-muted hover:text-red-400 transition-colors p-1.5" aria-label="Delete signup">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight mb-5">
          {editId ? "Edit Sponsor" : "Add Sponsor"}
        </h2>
        <form data-testid="sponsor-form" onSubmit={save} className="border border-clay-border bg-clay-card p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input data-testid="sponsor-name-input" placeholder="Business name *" value={form.name} onChange={set("name")} className={inputCls} required />
          <input data-testid="sponsor-link-input" placeholder="Website or Facebook link (optional)" value={form.link} onChange={set("link")} className={inputCls} />
          <input data-testid="sponsor-contact-name-input" placeholder="Contact person" value={form.contact_name} onChange={set("contact_name")} className={inputCls} />
          <input data-testid="sponsor-phone-input" placeholder="Contact phone" value={form.phone} onChange={set("phone")} className={inputCls} />
          <input data-testid="sponsor-email-input" type="email" placeholder="Contact email (used for renewal reminders)" value={form.email} onChange={set("email")} className={inputCls} />
          <select data-testid="sponsor-package-select" value={form.package} onChange={set("package")} className={inputCls}>
            <option value="">Package: not set</option>
            <option value="Gold">Gold ($1,000 / 2 seasons)</option>
            <option value="Silver">Silver ($600 / season)</option>
            <option value="Fence Signage">Fence Signage</option>
            <option value="Other">Other / Custom</option>
          </select>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted block mb-1.5">Joined</label>
            <input data-testid="sponsor-joined-input" type="date" value={form.joined_at} onChange={set("joined_at")} className={inputCls} />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted block mb-1.5">Expires (renewal reminders auto-send 30 days before)</label>
            <input data-testid="sponsor-expires-input" type="date" value={form.expires_at} onChange={set("expires_at")} className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <label className="flex items-center gap-3 cursor-pointer select-none font-mono text-[11px] uppercase tracking-[0.2em] text-clay-muted">
              <input
                data-testid="sponsor-contra-checkbox"
                type="checkbox"
                checked={form.contra}
                onChange={(e) => setForm((f) => ({ ...f, contra: e.target.checked }))}
                className="w-4 h-4 accent-orange-600"
              />
              Contra deal (goods/services instead of cash — counts as active, not a cash payment)
            </label>
          </div>
          <div className="sm:col-span-2">
            <input
              data-testid="sponsor-logo-input"
              type="file"
              accept="image/*"
              onChange={(e) => setLogo(e.target.files?.[0] || null)}
              className="w-full text-sm text-clay-muted file:mr-4 file:py-3 file:px-6 file:border-0 file:bg-clay-elevated file:text-clay-text file:font-display file:uppercase file:tracking-wider hover:file:bg-clay-orange hover:file:text-clay-bg file:transition-colors file:cursor-pointer"
            />
          </div>
          <input data-testid="sponsor-logourl-input" placeholder="…or paste a logo URL" value={form.logo_url} onChange={set("logo_url")} className={`${inputCls} sm:col-span-2`} />
          <div className="sm:col-span-2 flex gap-3">
            {editId && (
              <button
                data-testid="sponsor-cancel-button"
                type="button"
                onClick={() => { setEditId(null); setForm(EMPTY); setLogo(null); }}
                className="flex-1 border border-clay-border hover:border-clay-orange font-display font-bold uppercase tracking-wider px-6 py-4 transition-colors flex items-center justify-center gap-2"
              >
                <X className="w-5 h-5" /> Cancel
              </button>
            )}
            <button
              data-testid="sponsor-save-button"
              disabled={saving}
              className="flex-[2] bg-clay-orange hover:bg-clay-gold disabled:opacity-60 text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-6 py-4 transition-colors flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserPlus className="w-5 h-5" />}
              {editId ? "Save Changes" : "Add Sponsor"}
            </button>
          </div>
        </form>
      </section>

      <section>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight">
            Current Sponsors <span className="text-clay-muted text-xl">({current.length})</span>
          </h2>
          <div className="flex flex-wrap gap-2">
            {[["current", "CSV: Current"], ["financial", "CSV: Financial"], ["unfinancial", "CSV: Not Financial"]].map(([kind, label]) => (
              <button
                key={kind}
                data-testid={`sponsor-export-${kind}`}
                onClick={() => exportCsv(kind)}
                className="border border-clay-border hover:border-clay-orange text-clay-muted hover:text-clay-text font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 transition-colors"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted mb-4">
          Sponsors not renewed by their renewal date are hidden from the site and moved to Historic automatically
        </p>
        {current.length === 0 ? (
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay-muted border border-dashed border-clay-border py-12 text-center">No current sponsors</p>
        ) : (
          <div className="border border-clay-border overflow-x-auto">
            <table data-testid="sponsor-current-table" className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted border-b border-clay-border">
                  <th className="px-4 py-3">Sponsor</th>
                  <th className="px-4 py-3">Package</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Paid On</th>
                  <th className="px-4 py-3">Renewal Due</th>
                  <th className="px-4 py-3">Contact Details</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>{current.map((s) => sponsorRow(s, false))}</tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight mb-5">
          Historic Sponsors <span className="text-clay-muted text-xl">({historic.length})</span>
        </h2>
        {historic.length === 0 ? (
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay-muted border border-dashed border-clay-border py-12 text-center">No historic sponsors</p>
        ) : (
          <div className="border border-clay-border overflow-x-auto opacity-80">
            <table data-testid="sponsor-historic-table" className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted border-b border-clay-border">
                  <th className="px-4 py-3">Sponsor</th>
                  <th className="px-4 py-3">Package</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Paid On</th>
                  <th className="px-4 py-3">Lapsed</th>
                  <th className="px-4 py-3">Contact Details</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>{historic.map((s) => sponsorRow(s, true))}</tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
